import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, BookOpenText, X, DotsSixVertical } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InlineEdit } from '@/components/DraggableList';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminOnboardingPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [allContent, setAllContent] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [sections, setSections] = useState([]);
  const [showAddTag, setShowAddTag] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(true);
  // Drag state
  const [dragSec, setDragSec] = useState(null);
  const [overSec, setOverSec] = useState(null);
  const [dragItem, setDragItem] = useState(null); // {si, ii}
  const [overItem, setOverItem] = useState(null); // {si, ii}

  const fetchAll = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/program-onboarding`); setAllContent(data); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openTag = (tag) => {
    setSelectedTag(tag);
    const content = allContent.find(c => c.tag === tag);
    setSections(content?.sections?.map(s => ({ ...s, items: [...s.items] })) || []);
  };

  const addSection = () => setSections([...sections, { title: 'Nova secção', items: [''] }]);
  const removeSection = (si) => setSections(sections.filter((_, i) => i !== si));
  const updateSectionTitle = (si, title) => { const u = [...sections]; u[si] = { ...u[si], title }; setSections(u); };
  const addItem = (si) => { const u = [...sections]; u[si] = { ...u[si], items: [...u[si].items, ''] }; setSections(u); };
  const updateItem = (si, ii, val) => { const u = [...sections]; u[si] = { ...u[si], items: [...u[si].items] }; u[si].items[ii] = val; setSections(u); };
  const removeItem = (si, ii) => { const u = [...sections]; u[si] = { ...u[si], items: u[si].items.filter((_, i) => i !== ii) }; setSections(u); };

  // Section drag
  const handleSecDragStart = (e, si) => { setDragSec(si); e.dataTransfer.effectAllowed = 'move'; };
  const handleSecDragOver = (e, si) => { e.preventDefault(); setOverSec(si); };
  const handleSecDrop = (e, si) => {
    e.preventDefault(); setOverSec(null);
    if (dragSec === null || dragSec === si) { setDragSec(null); return; }
    const arr = [...sections];
    const [moved] = arr.splice(dragSec, 1);
    arr.splice(si > dragSec ? si - 1 : si, 0, moved);
    setSections(arr); setDragSec(null);
  };
  const handleSecDragEnd = () => { setDragSec(null); setOverSec(null); };

  // Item drag within section
  const handleItemDragStart = (e, si, ii) => { e.stopPropagation(); setDragItem({ si, ii }); e.dataTransfer.effectAllowed = 'move'; };
  const handleItemDragOver = (e, si, ii) => { e.preventDefault(); e.stopPropagation(); setOverItem({ si, ii }); };
  const handleItemDrop = (e, targetSi, targetIi) => {
    e.preventDefault(); e.stopPropagation(); setOverItem(null);
    if (!dragItem) return;
    const { si: srcSi, ii: srcIi } = dragItem;
    if (srcSi === targetSi && srcIi === targetIi) { setDragItem(null); return; }
    const s = sections.map(sec => ({ ...sec, items: [...sec.items] }));
    const [moved] = s[srcSi].items.splice(srcIi, 1);
    const insertIdx = srcSi === targetSi && targetIi > srcIi ? targetIi - 1 : targetIi;
    s[targetSi].items.splice(insertIdx, 0, moved);
    setSections(s); setDragItem(null);
  };
  const handleItemDragEnd = () => { setDragItem(null); setOverItem(null); };

  const save = async () => {
    const cleaned = sections.map(s => ({ title: s.title, items: s.items.filter(i => i.trim()) }));
    try { await axios.put(`${API}/api/program-onboarding/${selectedTag}`, { sections: cleaned }); toast.success('Conteúdo guardado'); fetchAll(); } catch { toast.error('Erro'); }
  };

  const deleteTag = async (tag) => {
    const ok = await confirmDelete({ description: `Vai apagar todo o conteúdo de onboarding da tag "${tag}".` });
    if (!ok) return;
    await axios.delete(`${API}/api/program-onboarding/${tag}`); toast.success('Eliminado'); setSelectedTag(null); fetchAll();
  };

  const createTag = async () => {
    if (!newTag.trim()) return;
    const tagKey = newTag.trim().toLowerCase().replace(/\s+/g, '_');
    await axios.put(`${API}/api/program-onboarding/${tagKey}`, { sections: [{ title: 'Bem-vinda ao programa', items: ['Conteúdo por definir'] }] });
    toast.success('Tag criada'); setShowAddTag(false); setNewTag(''); fetchAll();
  };

  // Detail view
  if (selectedTag) {
    return (
      <div className="fade-in" data-testid="onboarding-editor">
        <button onClick={() => setSelectedTag(null)} className="text-sm text-brand-green mb-3 hover:underline">&larr; Voltar</button>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-heading font-medium text-2xl text-brand-text">{selectedTag}</h2>
            <p className="text-sm text-brand-muted">Editar conteúdo de onboarding</p>
          </div>
          <button onClick={() => deleteTag(selectedTag)} className="text-xs text-red-500 border border-red-200 rounded-full px-3 py-1.5">Eliminar</button>
        </div>

        <div className="space-y-4 mb-5">
          {sections.map((sec, si) => (
            <div key={si}
              draggable onDragStart={e => handleSecDragStart(e, si)} onDragEnd={handleSecDragEnd}
              onDragOver={e => { if (dragSec !== null) handleSecDragOver(e, si); }}
              onDrop={e => { if (dragSec !== null) handleSecDrop(e, si); }}
              className={`bg-white border rounded-2xl p-4 transition-all ${dragSec === si ? 'opacity-40' : ''} ${overSec === si && dragSec !== null && dragSec !== si ? 'border-brand-green border-2 bg-brand-green/5' : 'border-brand-border'}`}
              data-testid={`onboarding-section-${si}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="cursor-grab active:cursor-grabbing text-brand-placeholder hover:text-brand-green"><DotsSixVertical size={14} /></div>
                <InlineEdit value={sec.title} onSave={v => updateSectionTitle(si, v)} className="flex-1 text-sm font-medium text-brand-text" inputClassName="rounded-lg border-brand-border text-sm font-medium h-8" testId={`sec-title-${si}`} />
                <button onClick={() => removeSection(si)} className="text-red-400 hover:text-red-600"><Trash size={14} /></button>
              </div>
              <div className="space-y-1.5 pl-1">
                {sec.items.map((item, ii) => (
                  <div key={ii}
                    draggable onDragStart={e => handleItemDragStart(e, si, ii)} onDragEnd={handleItemDragEnd}
                    onDragOver={e => handleItemDragOver(e, si, ii)} onDrop={e => handleItemDrop(e, si, ii)}
                    className={`flex items-center gap-2 transition-all ${dragItem?.si === si && dragItem?.ii === ii ? 'opacity-40' : ''} ${overItem?.si === si && overItem?.ii === ii && !(dragItem?.si === si && dragItem?.ii === ii) ? 'ring-1 ring-brand-green/40 rounded-lg bg-brand-green/5' : ''}`}>
                    <div className="cursor-grab active:cursor-grabbing text-brand-placeholder hover:text-brand-green"><DotsSixVertical size={10} /></div>
                    <span className="text-[10px] text-brand-muted w-4">{ii + 1}.</span>
                    <Input value={item} onChange={e => updateItem(si, ii, e.target.value)} className="flex-1 rounded-lg border-brand-border text-xs h-7" placeholder="Item da checklist..." />
                    <button onClick={() => removeItem(si, ii)} className="text-brand-placeholder hover:text-red-500"><X size={12} /></button>
                  </div>
                ))}
                <button onClick={() => addItem(si)} className="text-[10px] text-brand-green flex items-center gap-1 mt-1"><Plus size={10} /> Adicionar item</button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button onClick={addSection} className="flex-1 border border-brand-border text-brand-green rounded-full py-2.5 text-sm font-medium flex items-center justify-center gap-1"><Plus size={14} /> Nova secção</button>
          <button onClick={save} className="flex-1 bg-brand-green text-white rounded-full py-2.5 text-sm font-medium">Guardar</button>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="fade-in" data-testid="admin-onboarding-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-medium text-2xl text-brand-text">Onboarding por programa</h2>
          <p className="text-sm text-brand-muted mt-1">Conteúdo que cada cliente vê no separador "O meu programa"</p>
        </div>
        <button onClick={() => setShowAddTag(true)} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="add-onboarding-tag"><Plus size={16} /> Nova tag</button>
      </div>

      {loading ? <div className="text-center py-12 text-brand-muted">A carregar...</div> : allContent.length === 0 ? (
        <div className="text-center py-16"><BookOpenText size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" /><p className="text-brand-muted">Sem conteúdos de onboarding.</p></div>
      ) : (
        <div className="space-y-3">
          {allContent.map(c => (
            <button key={c.tag} onClick={() => openTag(c.tag)} className="w-full text-left bg-white border border-brand-border rounded-2xl p-4 hover:border-brand-green/30 transition-all" data-testid={`onboarding-tag-${c.tag}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-medium text-brand-text">{c.tag}</h3>
                  <p className="text-xs text-brand-muted mt-0.5">{c.sections?.length || 0} secções</p>
                </div>
                <PencilSimple size={16} className="text-brand-placeholder" />
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={showAddTag} onOpenChange={setShowAddTag}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Nova tag de onboarding</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome da tag</Label><Input value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="Ex: pediatria, fertilidade" className="rounded-xl border-brand-border" onKeyDown={e => { if (e.key === 'Enter') createTag(); }} /></div>
            <button onClick={createTag} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Criar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
